import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import {
  SelectFilterView,
  SelectFilterViewOptions,
} from '@/components/FilterControls/Select'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { type SingleSelectFilter } from '@/infra/SingleSelectFilter'

interface SingleSelectFilterViewContainerProps<
  TFilters extends Record<string, unknown>,
> {
  filter: SingleSelectFilter<keyof TFilters, unknown>
  testId?: string
}

/**
 * Editor for a {@link SingleSelectFilter}.
 */
export const SingleSelectFilterViewContainer = observer(
  function SingleSelectFilterViewContainer<
    TFilters extends Record<string, unknown>,
  >({ filter, testId }: SingleSelectFilterViewContainerProps<TFilters>) {
    useActivation(filter)

    return (
      <SelectFilterView
        title={filter.title}
        onClear={filter.clear}
        onSearchChange={
          filter.options.length >= 5 ? filter.changeSearchText : undefined
        }
        testId={testId}
      >
        {filter.fetchOptions.match({
          pending: () => <ContentLoading size="compact" />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => (
            <SelectFilterViewOptions
              options={filter.searchFilterOptions.map((o) => ({
                value: o.value,
                title: o.title,
                description: o.description,
                isSelected: filter.isSelected(o.value),
              }))}
              onOptionPress={filter.change}
              testId={testId && `${testId}-option`}
            />
          ),
        })}
      </SelectFilterView>
    )
  },
)
